import { DomainIpFormValue, DomainIpItem } from '@nirvana/shared/dtos/types/policies/common';
import { html, TemplateResult } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_ANTI_SPOOFING_CONTAINER } from 'apps/webcomponents/src/webcomponents.constants';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { InputWidthValues } from '../../../../../../../../ui/gzInput';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../ui/gzChipsGeneratorInput';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from 'apps/webcomponents/src/ui/gzForm/formGroup/form-group';
import _ from 'lodash';
import { RequiredValidator } from 'apps/webcomponents/src/ui/gzForm/validators/required.validator';
import { DomainValidator } from 'apps/webcomponents/src/ui/gzForm/validators/domain.validator';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { IpValidator } from '../../../../../../../../ui/gzForm/validators/ip.validator';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_ANTI_SPOOFING_CONTAINER)
export class GzExchangeProtectionAntiSpoofingContainerWebComponent extends GzPolicyFullPageWindowFormWebComponent<DomainIpItem> {
  @query('#domain-form-field')
  domain: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#ips-chips-generator')
  ipsChipsGenerator: GzChipsGeneratorInputWebComponent;

  @query('#ips-form-field')
  ips: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;

  static get styles() {
    return styles;
  }
  private domainIpForm?: DomainIpItem;
  private usedDomains: string[];
  constructor(domainIpItems: DomainIpItem[], domainIpForm?: DomainIpItem) {
    super();
    this.domainIpForm = domainIpForm;
    this.usedDomains = domainIpItems.map(item => item.domain).filter(domain => domain !== domainIpForm?.domain);
  }

  protected createFormGroup(): void {
    this.domain.Validators = [
      RequiredValidator(translateFn('Required field')),
      DomainValidator(translateFn('Invalid domain name')),
      CustomizableValidator(this.validateDomainDuplicates.bind(this)),
    ];
    this.ips.Validators = [RequiredValidator(translateFn('Required field'))];

    this.formGroup = new FormGroup({
      domain: this.domain,
      ips: this.ips,
    });
  }

  protected setFormGroupValue(): void {
    if (this.domainIpForm) {
      this.formGroup.value = {
        domain: this.domainIpForm.domain,
        ips: this.domainIpForm.ips,
      };
    } else {
      this.formGroup.value = {
        domain: '',
        ips: [],
      };
    }
  }

  protected getConvertedFormValue(): DomainIpItem {
    const formValue = this.formGroup.value;
    const ips = _.get(formValue, 'ips', []) as string[];
    const domain = _.get(formValue, 'domain', '') as string;
    return {
      domain: domain,
      ips: ips,
      ipsCount: ips.length,
      ipsString: ips.join('; '),
    };
  }

  protected renderContent(): TemplateResult {
    return html`<div style="padding-top: calc(var(--spacer) * 2)">
      <gz-form-field class="label-mw-250" id="domain-form-field" label="${translateFn('Domain name')}" required>
        <gz-input inputWidth="${InputWidthValues.LARGE}" placeholder="${translateFn('Domain name')}" gz-form-input>
        </gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id="ips-form-field">
        <gz-chips-generator-input
          id="ips-chips-generator"
          gz-form-input
          hasFloatingLabel
          .validators=${[IpValidator('Invalid IP/mask format')]}
          .strings="${{
            label: translateFn('IP / Mask'),
            clearListButtonText: translateFn('Clear list'),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Remove'),
            deleteWindowTitle: translateFn('Remove IP addresses?'),
            hintText: translateFn(
              'Specify the IP addresses authorized to send emails for the above domain, using the CIDR format. Example: 10.1.1.0/24 '
            ),
            deleteWindowContentText: translateFn(
              'You are about to remove all IP addresses from the list. This action cannot be undone.'
            ),
            notUniqueErrorMessage: translateFn('The entry already exists.'),
          }}"
          style="--chip-list-width: 490px;
        --form-field-with-floating-max-width: 490px;"
        >
        </gz-chips-generator-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
    </div>`;
  }
  private async validateDomainDuplicates(domain: string) {
    if (this.usedDomains.includes(domain)) {
      return {
        valid: false,
        error: translateFn('The name already exists.'),
      };
    }
    return { valid: true };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_ANTI_SPOOFING_CONTAINER: GzExchangeProtectionAntiSpoofingContainerWebComponent;
  }
}
